// UtilityCluster.js
//
// Floating top-right cluster of infrequent-but-important actions:
//   - Undo / Redo (disabled when there's nothing to undo/redo)
//   - Kebab menu: Export Data, Upload Shapefile, Capture Screenshot,
//     Clear Markers (destructive, confirmation happens in the parent)
//
// These actions don't share much behaviorally — Undo/Redo are one-click,
// the rest open dialogs or file pickers — so we group them here purely by
// "utility" discoverability rather than workflow. The kebab keeps the
// less-frequent ones off the main canvas.
//
// All click-out / Escape handling is local to the kebab to keep this
// component self-contained; the parent only supplies handlers.

import React from 'react';
import { Undo2, Redo2, MoreHorizontal, Download, Upload, Camera, Trash2 } from 'lucide-react';

const clusterStyle = {
  position: 'fixed',
  top: '12px',
  right: '12px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 6px',
  backgroundColor: 'rgba(255,255,255,0.96)',
  border: '1px solid #cfd7d0',
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 1200,
};

const iconBtnStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  padding: 0,
  border: 'none',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  color: disabled ? '#bbc' : '#223',
  cursor: disabled ? 'default' : 'pointer',
  transition: 'background-color 120ms ease',
});

const menuStyle = {
  position: 'absolute',
  top: '44px',
  right: 0,
  width: '240px',
  backgroundColor: 'white',
  border: '1px solid #cfd7d0',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
  padding: '6px 0',
  zIndex: 1250,
};

const menuItemStyle = (variant = 'default') => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '8px 14px',
  border: 'none',
  borderRadius: '0',
  backgroundColor: 'transparent',
  color: variant === 'danger' ? '#a02522' : '#223',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  fontWeight: 500,
  textAlign: 'left',
  cursor: 'pointer',
  boxSizing: 'border-box',
});

// Filename cell that truncates with ellipsis, then shows the full name in a
// custom popover on hover (no native-tooltip delay). Anchored to the cell
// so long names don't get clipped by the kebab menu's fixed width.
const ShapefileNameCell = ({ filename }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        fontSize: '0.72rem',
        color: '#223',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'default',
      }}
    >
      {filename}
      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            padding: '4px 8px',
            backgroundColor: '#223',
            color: 'white',
            fontSize: '0.7rem',
            fontWeight: 500,
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            whiteSpace: 'nowrap',
            zIndex: 1300,
            pointerEvents: 'none',
          }}
        >
          {filename}
        </div>
      )}
    </div>
  );
};

const UtilityCluster = ({
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onExport,
  onUploadShapefile,
  onCaptureScreenshot,
  onClearMarkers,
  // View toggles that used to live in the legacy top-right overlay.
  // Each entry: { key, label, checked, onChange }. Rendered as
  // menu-item checkboxes at the top of the kebab menu so visibility
  // knobs are one click away without cluttering the canvas.
  toggles = [],
  // Optional shapefile list: [{ id, filename, color, uploadTime,
  // visible }] plus onToggleVisible(id, bool) and onRemove(id).
  // Rendered inside the kebab menu when non-empty.
  shapefiles = [],
  shapefileOpacity = 1,
  onShapefileOpacityChange,
  onToggleShapefileVisible,
  onRemoveShapefile,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  // Close the kebab menu on outside click / Escape. Scoped to
  // `containerRef` so the menu can still receive clicks on its own items.
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const hoverBg = (e, enter) => {
    if (enter) e.currentTarget.style.backgroundColor = '#eef4ee';
    else e.currentTarget.style.backgroundColor = 'transparent';
  };

  const runAndClose = (fn) => () => {
    setMenuOpen(false);
    if (typeof fn === 'function') fn();
  };

  return (
    <div ref={containerRef} style={{ ...clusterStyle, position: 'fixed' }}>
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo (⌘/Ctrl+Z)"
        style={iconBtnStyle(!canUndo)}
        onMouseEnter={(e) => { if (canUndo) hoverBg(e, true); }}
        onMouseLeave={(e) => hoverBg(e, false)}
      >
        <Undo2 size={18} strokeWidth={1.75} />
      </button>

      <button
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        title="Redo (⌘/Ctrl+Shift+Z)"
        style={iconBtnStyle(!canRedo)}
        onMouseEnter={(e) => { if (canRedo) hoverBg(e, true); }}
        onMouseLeave={(e) => hoverBg(e, false)}
      >
        <Redo2 size={18} strokeWidth={1.75} />
      </button>

      <div style={{ width: 1, height: 20, backgroundColor: '#e4e8e4', margin: '0 2px' }} />

      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          title="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={iconBtnStyle(false)}
          onMouseEnter={(e) => hoverBg(e, true)}
          onMouseLeave={(e) => hoverBg(e, false)}
        >
          <MoreHorizontal size={18} strokeWidth={1.75} />
        </button>

        {menuOpen && (
          <div role="menu" style={menuStyle}>
            {/* View toggles (formerly the right-side overlay checkboxes).
                Rendered as rows with a label + checkbox; clicking the row
                toggles the value. Kept at the top of the menu because
                they're the most-used items. */}
            {toggles.length > 0 && (
              <>
                {toggles.map((t) => (
                  <label
                    key={t.key}
                    role="menuitemcheckbox"
                    aria-checked={!!t.checked}
                    style={{
                      ...menuItemStyle(),
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      padding: '8px 14px',
                    }}
                    onMouseEnter={(e) => hoverBg(e, true)}
                    onMouseLeave={(e) => hoverBg(e, false)}
                  >
                    <span>{t.label}</span>
                    <input
                      type="checkbox"
                      checked={!!t.checked}
                      onChange={(e) => t.onChange && t.onChange(e)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                  </label>
                ))}
                <div style={{ height: 1, backgroundColor: '#e4e8e4', margin: '6px 0' }} />
              </>
            )}

            {/* Shapefile list (if any). Each row: visibility checkbox,
                filename + upload time, remove button. Previously lived
                as its own block below the toggles in the legacy overlay. */}
            {shapefiles.length > 0 && (
              <>
                <div style={{ padding: '6px 10px 2px', fontSize: '0.72rem', color: '#556', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Shapefiles ({shapefiles.length})
                </div>
                {/* Global opacity slider — dims all shapefile lines/dots
                    so the user's own work stays visually dominant. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 6px' }}>
                  <span style={{ fontSize: '0.72rem', color: '#556' }}>Dim</span>
                  <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.05}
                    value={shapefileOpacity}
                    onChange={(e) => onShapefileOpacityChange && onShapefileOpacityChange(parseFloat(e.target.value))}
                    title="Adjust shapefile visibility"
                    style={{ flex: 1, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.7rem', color: '#667', minWidth: 28, textAlign: 'right' }}>
                    {Math.round(shapefileOpacity * 100)}%
                  </span>
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto', padding: '2px' }}>
                  {shapefiles.map((sf) => (
                    <div key={sf.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 6px',
                      borderRadius: 4,
                    }}>
                      <input
                        type="checkbox"
                        title="Toggle visibility"
                        checked={sf.visible !== false}
                        onChange={(e) => onToggleShapefileVisible && onToggleShapefileVisible(sf.id, e.target.checked)}
                        style={{ width: 14, height: 14, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <ShapefileNameCell filename={sf.filename} />
                        <div style={{ fontSize: '0.65rem', color: '#667' }}>
                          <span style={{ color: sf.color, marginRight: 6 }}>●</span>{sf.uploadTime}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveShapefile && onRemoveShapefile(sf.id)}
                        title="Remove shapefile"
                        style={{
                          fontSize: '0.7rem',
                          padding: '2px 6px',
                          backgroundColor: '#fdeceb',
                          color: '#a02522',
                          border: 'none',
                          borderRadius: 3,
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                      >×</button>
                    </div>
                  ))}
                </div>
                <div style={{ height: 1, backgroundColor: '#e4e8e4', margin: '6px 0' }} />
              </>
            )}

            <button role="menuitem" type="button" onClick={runAndClose(onExport)}
              style={menuItemStyle()}
              onMouseEnter={(e) => hoverBg(e, true)} onMouseLeave={(e) => hoverBg(e, false)}>
              <Download size={15} strokeWidth={1.75} /> Export data…
            </button>
            <button role="menuitem" type="button" onClick={runAndClose(onUploadShapefile)}
              style={menuItemStyle()}
              onMouseEnter={(e) => hoverBg(e, true)} onMouseLeave={(e) => hoverBg(e, false)}>
              <Upload size={15} strokeWidth={1.75} /> Upload shapefile…
            </button>
            <button role="menuitem" type="button" onClick={runAndClose(onCaptureScreenshot)}
              style={menuItemStyle()}
              onMouseEnter={(e) => hoverBg(e, true)} onMouseLeave={(e) => hoverBg(e, false)}>
              <Camera size={15} strokeWidth={1.75} /> Capture screenshot
            </button>
            <div style={{ height: 1, backgroundColor: '#e4e8e4', margin: '6px 0' }} />
            <button role="menuitem" type="button" onClick={runAndClose(onClearMarkers)}
              style={menuItemStyle('danger')}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fdeceb'; }}
              onMouseLeave={(e) => hoverBg(e, false)}>
              <Trash2 size={15} strokeWidth={1.75} /> Clear markers
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UtilityCluster;
